import { reactive, readonly } from 'vue';

/**
 * 2.7 — arrastar e soltar, **e o caminho sem arrastar** (D17, D11).
 *
 * ## O problema, dito pelo D17
 *
 * > Arrastar no toque é chato: o dedo cobre o item, o navegador confunde com rolagem, e não há
 * > hover para mostrar onde solta.
 *
 * As três queixas têm resposta diferente, e é por isso que este arquivo não é um `draggable=true`:
 *
 * | Queixa | Resposta |
 * |---|---|
 * | o dedo cobre o item | o fantasma segue o dedo **deslocado para cima** (`DESLOCAMENTO_DO_DEDO`) |
 * | o navegador confunde com rolagem | **pressionar e segurar** por 220ms antes de levantar |
 * | não há hover | os encaixes válidos **acendem** quando algo é levantado |
 *
 * ## A regra que não pode ser esquecida (D11)
 *
 * > **E sempre existe o caminho sem arrastar:** tocar no item → os encaixes válidos acendem →
 * > tocar no encaixe. Mesmo resultado, dois toques, sem arrastar. **Arrastar nunca pode ser o único
 * > caminho.**
 *
 * É o mesmo estado (`levantado`) que serve aos dois: arrastar e o toque duplo escrevem nele, e a
 * tela só pergunta "o que está levantado?". Ter dois estados seria garantir que um dia eles
 * discordem.
 *
 * ## E arrastar tem que perdoar
 *
 * > Soltar fora de um encaixe válido devolve o item para onde ele estava, animado. **Nunca some,
 * > nunca apaga.**
 *
 * `soltar()` sem alvo válido não emite nada e devolve `false` — a tela anima a volta. Não há um
 * caminho neste arquivo em que um item levantado deixe de existir.
 *
 * ## Por que não é um composable com `use` de verdade
 *
 * O estado é **único por página**: só se levanta um item por vez. Um `useDragDrop()` que criasse
 * estado novo a cada chamada faria a mochila e o cantinho do pescador terem opiniões diferentes
 * sobre o que está na mão. O módulo exporta uma instância, e é isso que se quer.
 */

/** Quanto tempo o dedo fica parado antes de o item levantar. Abaixo disso, rolar viraria arrastar. */
const ESPERA_PARA_LEVANTAR = 220;

/** Quanto o dedo pode deslizar durante a espera sem cancelar. Dedo parado não fica parado. */
const TOLERANCIA_DE_DESLIZE = 10;

/** O fantasma sobe acima do dedo — senão o dedo cobre exatamente o que se está tentando ver. */
const DESLOCAMENTO_DO_DEDO = 48;

const estado = reactive({
  /** O item na mão, ou `null`. É a única fonte de verdade dos dois caminhos. */
  levantado: null,
  /** De onde ele saiu — para devolver, e para o encaixe de origem se apagar. */
  origem: null,
  /** Os ids de encaixe que aceitam o levantado. É o que faz os encaixes acenderem. */
  alvosValidos: [],
  /** Está arrastando de verdade (dedo ou mouse na tela), contra "levantado por dois toques". */
  arrastando: false,
  fantasma: { x: 0, y: 0, visivel: false },
});

let temporizador = null;
let inicio = null;
let aoSoltarCallback = null;

function limparEspera() {
  if (temporizador) clearTimeout(temporizador);
  temporizador = null;
}

/**
 * Levanta um item. Chamado pelos dois caminhos: pelo toque longo e pelo primeiro toque do caminho
 * de dois toques.
 *
 * `alvosValidos` vem de quem chama, não é deduzido aqui: só a tela sabe que uma vara serve no
 * encaixe de vara e não no de barco. Um primitivo que soubesse isso teria virado dependente de tela
 * (D4).
 */
function levantar({ item, origem, alvosValidos = [], aoSoltar = null }) {
  estado.levantado = item;
  estado.origem = origem ?? null;
  estado.alvosValidos = alvosValidos;
  aoSoltarCallback = aoSoltar;
}

function largar() {
  limparEspera();
  estado.levantado = null;
  estado.origem = null;
  estado.alvosValidos = [];
  estado.arrastando = false;
  estado.fantasma.visivel = false;
  aoSoltarCallback = null;
  inicio = null;
}

/**
 * Solta no alvo. Devolve `true` se o alvo era válido.
 *
 * O `false` é o caminho do perdão: quem chama anima a volta do item para a origem. Nada é apagado
 * aqui, em nenhum caminho.
 */
function soltar(alvo) {
  if (!estado.levantado) return false;

  const valido = alvo != null && estado.alvosValidos.includes(alvo);
  if (valido && aoSoltarCallback) aoSoltarCallback({ item: estado.levantado, origem: estado.origem, alvo });

  const item = estado.levantado;
  largar();
  return valido ? { item, alvo } : false;
}

/** O primeiro toque do caminho de dois toques (D11) — sem arrastar, sem espera. */
function tocarItem(carga) {
  // Tocar de novo no mesmo item larga: senão o único jeito de desistir seria tocar num alvo válido,
  // e o jogador que mudou de ideia ficaria preso segurando algo.
  if (estado.levantado && estado.levantado === carga.item) {
    largar();
    return;
  }
  levantar(carga);
}

/**
 * O toque longo, que inicia o arrasto de verdade.
 *
 * Devolve os manipuladores para o componente pendurar no encaixe. O `passive: false` do
 * `touchmove` é obrigatório: sem ele o `preventDefault` não pega, e a página rola debaixo do item.
 */
function aoPressionar(evento, carga) {
  const ponto = evento.touches ? evento.touches[0] : evento;
  inicio = { x: ponto.clientX, y: ponto.clientY };

  limparEspera();
  temporizador = setTimeout(() => {
    levantar(carga);
    estado.arrastando = true;
    estado.fantasma = { x: inicio.x, y: inicio.y - DESLOCAMENTO_DO_DEDO, visivel: true };
  }, ESPERA_PARA_LEVANTAR);
}

function aoMover(evento) {
  const ponto = evento.touches ? evento.touches[0] : evento;

  // Ainda na espera: se o dedo andou, era rolagem. Cancela e deixa a página rolar.
  if (temporizador && inicio) {
    const andou =
      Math.abs(ponto.clientX - inicio.x) > TOLERANCIA_DE_DESLIZE ||
      Math.abs(ponto.clientY - inicio.y) > TOLERANCIA_DE_DESLIZE;
    if (andou) limparEspera();
    return;
  }

  if (!estado.arrastando) return;

  // Já arrastando: agora a rolagem é que atrapalha, então ela é barrada.
  if (evento.cancelable) evento.preventDefault();
  estado.fantasma = { x: ponto.clientX, y: ponto.clientY - DESLOCAMENTO_DO_DEDO, visivel: true };
}

/**
 * O dedo levantou. Descobre o alvo por baixo dele.
 *
 * `elementFromPoint` e não o alvo do evento: num `touchend`, o `target` é **o elemento onde o toque
 * COMEÇOU**, não onde terminou. Usar o `target` faria todo arrasto soltar na origem — o bug clássico
 * de arrastar no toque, e ele parece "às vezes não funciona".
 */
function aoLargar(evento) {
  limparEspera();

  if (!estado.arrastando) {
    // Foi um toque curto, não um arrasto: cai no caminho de dois toques, que é o D11.
    return null;
  }

  const ponto = evento.changedTouches ? evento.changedTouches[0] : evento;
  const sob = document.elementFromPoint(ponto.clientX, ponto.clientY);
  const alvo = sob?.closest('[data-encaixe]')?.dataset.encaixe ?? null;

  return soltar(alvo);
}

export const arrasto = {
  estado: readonly(estado),
  levantar,
  largar,
  soltar,
  tocarItem,
  aoPressionar,
  aoMover,
  aoLargar,
  /** O encaixe pergunta isto para saber se acende (D17). */
  aceita: (id) => estado.levantado != null && estado.alvosValidos.includes(id),
  /** O encaixe de origem pergunta isto para se apagar durante o arrasto. */
  ehOrigem: (id) => estado.arrastando && estado.origem === id,
};

export default arrasto;
